import React from 'react';
import clsx from 'clsx';

const Sidebar = ({
  children,
  collapsed = false,
  className,
  variant = 'default',
  ...props
}) => {
  const variants = {
    default: 'bg-white border-r border-gray-200',
    dark: 'bg-gray-900 border-r border-gray-700',
    branded: 'bg-blue-900 border-r border-blue-800'
  };

  const sidebarClasses = clsx(
    'h-full flex flex-col',
    variants[variant],
    className
  );

  return (
    <div className={sidebarClasses} {...props}>
      {children}
    </div>
  );
};

const SidebarHeader = ({ 
  children, 
  className,
  logo,
  title,
  collapsed = false,
  ...props 
}) => {
  const headerClasses = clsx(
    'flex items-center px-4 py-4 border-b border-gray-200',
    className
  );

  return (
    <div className={headerClasses} {...props}>
      {logo && (
        <div className="flex-shrink-0">
          {typeof logo === 'string' ? (
            <img src={logo} alt={title} className="w-8 h-8" />
          ) : (
            logo
          )}
        </div>
      )}
      
      {!collapsed && title && (
        <h1 className="ml-3 text-lg font-semibold text-gray-900 truncate">
          {title}
        </h1>
      )}
      
      {!collapsed && children}
    </div>
  );
};

const SidebarBody = ({ children, className, ...props }) => {
  const bodyClasses = clsx(
    'flex-1 overflow-y-auto px-2 py-4',
    className
  );

  return (
    <div className={bodyClasses} {...props}>
      {children}
    </div>
  );
};

const SidebarFooter = ({ children, className, collapsed = false, ...props }) => {
  const footerClasses = clsx(
    'border-t border-gray-200 px-4 py-4',
    className
  );

  return (
    <div className={footerClasses} {...props}>
      {children}
    </div>
  );
};

const SidebarNav = ({ children, className, ...props }) => {
  const navClasses = clsx('space-y-1', className);

  return (
    <nav className={navClasses} {...props}>
      {children}
    </nav>
  );
};

const SidebarNavItem = ({
  children,
  href,
  active = false,
  icon,
  badge,
  collapsed = false,
  onClick,
  className,
  ...props
}) => {
  const itemClasses = clsx(
    'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
    active 
      ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-500' 
      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
    className
  );

  const Component = href ? 'a' : 'button';

  return (
    <Component
      href={href}
      onClick={onClick}
      className={itemClasses}
      {...props}
    >
      {icon && (
        <span className={clsx(
          'flex-shrink-0',
          collapsed ? 'mx-auto' : 'mr-3'
        )}>
          {icon}
        </span>
      )}
      
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{children}</span>
          {badge && (
            <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
              {badge}
            </span>
          )}
        </>
      )}
    </Component>
  );
};

const SidebarNavGroup = ({ 
  children, 
  title, 
  collapsed = false,
  className,
  ...props 
}) => {
  if (collapsed) {
    return <div className={className} {...props}>{children}</div>;
  }

  const groupClasses = clsx('space-y-1', className);

  return (
    <div className={groupClasses} {...props}>
      {title && (
        <h3 className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </h3>
      )}
      {children}
    </div>
  );
};

// User menu component for sidebar
const SidebarUser = ({
  user,
  collapsed = false,
  onProfileClick,
  onSignOut,
  className,
  ...props
}) => {
  if (!user) return null;

  const userClasses = clsx(
    'flex items-center px-3 py-2 text-sm',
    collapsed ? 'justify-center' : 'space-x-3',
    className
  );

  if (collapsed) {
    return (
      <div className={userClasses} {...props}>
        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
          ) : (
            <span className="text-gray-600 font-medium">
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={userClasses} {...props}>
      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
        {user.avatar ? (
          <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
        ) : (
          <span className="text-gray-600 font-medium">
            {user.name?.charAt(0)?.toUpperCase() || 'U'}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {user.name}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {user.email}
        </p>
      </div>
      {(onProfileClick || onSignOut) && (
        <div className="relative">
          <button className="text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

Sidebar.Header = SidebarHeader;
Sidebar.Body = SidebarBody;
Sidebar.Footer = SidebarFooter;
Sidebar.Nav = SidebarNav;
Sidebar.NavItem = SidebarNavItem;
Sidebar.NavGroup = SidebarNavGroup;
Sidebar.User = SidebarUser;

export default Sidebar;